import React from 'react';
import styled from '../../styles/styled-components';
import { List, ListItem } from '../list';
import { IconsKey, Glyphicon } from '../icons';
// @ts-ignore
import SyncroImgSrc from 'img/syncro-logo-temp.png';
// @ts-ignore
import KabutoImgSrc from 'img/logo.svg';
import { Link } from '@reach/router';
import { theme } from '../../styles/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

type SidebarContentProps = {
  isSyncro: boolean;
  assetId: string | undefined;
  onCloseClick: (ev: React.MouseEvent) => void;
} & Stylable;

type Path = {
  path: string;
  name: string;
  icon: IconsKey;
  stagingOnly?: boolean;
  syncroOnly?: boolean;
};

const getPathMaps = (isSyncro: boolean): Path[] => {
  var pathArray = [
    { path: '/tasks', name: 'Task Manager', icon: 'activity' },
    { path: '/services', name: 'Service Manager', icon: 'settings' },
    {
      path: '/event-viewer',
      name: 'Event Viewer',
      icon: 'alertOctagon',
      syncroOnly: true,
    },
    { path: '/info', name: 'System Information', icon: 'barChart2' },
    { path: '/filesystem', name: 'File System', icon: 'hardDrive' },
    {
      path: '/registry-editor',
      name: 'Registry Editor',
      icon: 'box',
      syncroOnly: true,
    },
    { path: '/screen', name: 'Remote Desktop', icon: 'eye' },
    { path: '/terminal', name: 'Terminal', icon: 'terminal' },
  ] as Path[];

  if (!isSyncro) {
    pathArray = pathArray.filter(x => !x.syncroOnly);
  }

  if (process.env.SYNCRO_ENV == 'production') {
    pathArray = pathArray.filter(x => !x.stagingOnly);
  }

  return pathArray;
};

export const SidebarContent: React.SFC<SidebarContentProps> = ({
  isSyncro,
  assetId,
  onCloseClick,
  ...rest
}) => (
  <SidebarContainer>
    <Title>
      <Logo src={isSyncro ? SyncroImgSrc : KabutoImgSrc} />
      <span>{isSyncro ? 'Syncro' : 'KABUTO'}</span>
      <CloseButtonWrapper>
        <CloseButton icon={faTimes} onClick={onCloseClick} />
      </CloseButtonWrapper>
    </Title>
    <SidebarList {...rest}>
      {getPathMaps(isSyncro).map(({ path, name, icon }) => (
        <SidebarItem key={path}>
          <SidebarLink
            to={!!assetId ? `${path}?asset_id=${assetId}` : path}
            getProps={({ isCurrent }) => ({
              style: {
                backgroundColor: isCurrent ? theme.primaryDarkColor : 'initial',
              },
            })}
          >
            <LinkIcon glyph={icon} />
            {name}
          </SidebarLink>
        </SidebarItem>
      ))}
    </SidebarList>
  </SidebarContainer>
);

const SidebarContainer = styled.div`
  background-color: ${p => p.theme.primaryColor};
  height: 100%;
  color: ${p => p.theme.whiteText};
  font-family: ProximaNova;
`;

const Title = styled.div`
  height: 50px;
  display: flex;
  align-items: center;
  font-family: Quicksand;
  font-size: 20px;
  padding: 0 10px;
`;

const CloseButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 1 1 auto;
  padding-right: 10px;
`;

const CloseButton = styled(FontAwesomeIcon)`
  cursor: pointer;
  align-self: center;
  transition: 0.2s ease all;

  &:hover {
    color: ${p => p.theme.primaryDarkerColor};
    transform: scale(1.2);
    transition: 0.2s ease all;
  }
`;

const Logo = styled.img`
  width: 25px;
  margin-right: 10px;
`;

const SidebarList = styled(List).attrs({
  itemsMargin: 2,
})``;

const SidebarItem = styled(ListItem)`
  padding: 0;
`;

const SidebarLink = styled(Link)`
  color: ${p => p.theme.whiteText};

  display: flex;
  align-items: center;
  padding: 10px;
  padding-right: 50px;

  &:visited {
    color: currentColor;
  }

  &:hover {
    color: currentColor;
    text-decoration: none;
    background-color: ${p => p.theme.primaryDarkerColor} !important;
  }

  &:focus {
    color: currentColor;
    text-decoration: none;
    background-color: ${p => p.theme.primaryDarkerColor} !important;
  }
`;

const LinkIcon = styled(Glyphicon)`
  margin-right: 15px;
`;
